import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";

import TextField from "@material-ui/core/TextField";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import { Grid, makeStyles, Box } from "@material-ui/core";
import { ArrowLeftRight } from "lucide-react";

import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useToast } from "../../hooks/useToast";
import useQueues from "../../hooks/useQueues";
import UserStatusIcon from "../UserModal/statusIcon";
import StandardModal from "../ui/StandardModal";

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
  maxWidth: {
    width: "100%",
  },
  root: {
    "& .MuiDialog-paper": {
      borderRadius: 12,
      overflow: "hidden",
    },
  },
  dialogTitle: {
    position: "relative",
    background: theme.palette.background.default,
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
    "& h2": {
      display: "flex",
      alignItems: "center",
      fontSize: "1rem",
    },
  },
  dialogContent: {
    padding: theme.spacing(2),
    minHeight: 90,
    backgroundColor: theme.palette.background.default,
  },
  dialogActions: {
    padding: theme.spacing(1, 2),
    background: theme.palette.background.default,
    borderTop: `1px solid ${theme.palette.divider}`,
    justifyContent: "flex-end",
  },
  formControl: {
    margin: theme.spacing(1, 0),
    minWidth: "100%",
  },
  selectMenu: {
    height: 40,
    minHeight: 40,
    borderRadius: 8,
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: 8,
    },
    "&.MuiOutlinedInput-root": {
      fontSize: "0.9rem",
      "&:hover fieldset": {
        borderColor: theme.palette.primary.light,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
  },
  menuItem: {
    fontSize: "0.9rem",
    height: 36,
    minHeight: 36,
    paddingTop: 4,
    paddingBottom: 4,
  },
  inputLabel: {
    fontSize: "0.9rem",
    transform: "translate(14px, 12px) scale(1)",
    "&.MuiInputLabel-shrink": {
      transform: "translate(14px, -6px) scale(0.75)",
    },
  },
  textField: {
    borderRadius: 8,
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: 8,
    },
    "& .MuiOutlinedInput-input": {
      padding: "10.5px 14px",
    },
  },
  autoComplete: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      height: 40,
      minHeight: 40,
      display: "flex",
      alignItems: "center",
    },
    "& .MuiAutocomplete-input": {
      padding: "0 0 !important",
      height: "auto",
    },
  },
  modalContent: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  formContainer: {
    width: "100%",
    margin: "0 auto",
  },
  queueColor: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    marginRight: 5,
    display: "inline-block",
    backgroundColor: dark ? "rgba(255,255,255,0.06)" : theme.palette.grey[200],
  },
  userStatus: {
    display: "flex",
    alignItems: "center",
    "& .MuiSvgIcon-root": {
      marginRight: 5,
    },
  },
  userStatusText: {
    fontSize: "0.85rem",
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  userContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
};
});

const filterOptions = createFilterOptions({
  trim: true,
});

const TransferTicketModalCustom = ({
  modalOpen,
  onClose,
  ticketid,
  ticket,
}) => {
  const history = useHistory();
  const toast = useToast();
  const [options, setOptions] = useState([]);
  const [queues, setQueues] = useState([]);
  const [allQueues, setAllQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState("");
  const { findAll: findAllQueues } = useQueues();
  const isMounted = useRef(true);
  const [msgTransfer, setMsgTransfer] = useState("");
  const classes = useStyles();

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      const loadQueues = async () => {
        const list = await findAllQueues();
        setAllQueues(list);
        setQueues(list);
      };
      loadQueues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!modalOpen || searchParam.length < 3) {
      setLoading(false);
      setSelectedQueue("");
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      setLoading(true);
      const fetchUsers = async () => {
        try {
          const { data } = await api.get("/users/", {
            params: { searchParam },
          });
          setOptions(data.users);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };

      fetchUsers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, modalOpen]);

  const handleMsgTransferChange = (event) => {
    setMsgTransfer(event.target.value);
  };

  const handleClose = () => {
    onClose();
    setSearchParam("");
    setSelectedUser(null);
  };

  const handleSaveTicket = async () => {
    if (!ticketid || !isMounted.current) return;
    if (!selectedQueue || selectedQueue === "") return;

    setLoading(true);
    try {
      let data = {};

      data.userId = !selectedUser ? null : selectedUser.id;
      data.status = !selectedUser
        ? "pending"
        : ticket.isGroup
        ? "group"
        : "open";
      data.queueId = selectedQueue;
      data.msgTransfer = msgTransfer ? msgTransfer : null;
      data.isTransfered = true;

      const { data: response } = await api.put(`/tickets/${ticketid}`, data);

      if (!isMounted.current) return;
      setLoading(false);

      if (response?.warnings?.length) {
        response.warnings.forEach((w) => toast.warning(w, { duration: 8000 }));
      }

      history.push(`/tickets/`);
      handleClose();
    } catch (err) {
      if (!isMounted.current) return;
      setLoading(false);
      toastError(err);
    }
  };

  return (
    <StandardModal
      open={modalOpen}
      onClose={handleClose}
      onConfirm={handleSaveTicket}
      title={i18n.t("transferTicketModal.title")}
      maxWidth="sm"
      titleIcon={<ArrowLeftRight size={20} />}
      confirmationText={i18n.t("transferTicketModal.buttons.ok")}
      cancelText={i18n.t("transferTicketModal.buttons.cancel")}
      loading={loading}
      disableConfirmButton={selectedQueue === ""}
      useButtonSpinner={true}
      disableBackdropClick={true}
      disableEscapeKeyDown={true}
    >
      <div
        className={classes.modalContent}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={classes.formContainer}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                getOptionLabel={(option) => `${option.name}`}
                onChange={(e, newValue) => {
                  setSelectedUser(newValue);
                  if (newValue != null && Array.isArray(newValue.queues)) {
                    if (newValue.queues.length === 1) {
                      setSelectedQueue(newValue.queues[0].id);
                    }
                    setQueues(newValue.queues);
                  } else {
                    setQueues(allQueues);
                    setSelectedQueue("");
                  }
                }}
                options={options}
                filterOptions={filterOptions}
                freeSolo
                autoHighlight
                noOptionsText={i18n.t("transferTicketModal.noOptions")}
                loading={loading}
                onClick={(e) => e.stopPropagation()}
                renderOption={(option) => (
                  <Box className={classes.userContainer}>
                    <Box className={classes.userStatus}>
                      <UserStatusIcon user={option} />
                      <span className={classes.userStatusText}>
                        {option.name}
                      </span>
                    </Box>
                    {option.email && (
                      <Typography variant="caption" color="textSecondary">
                        {option.email}
                      </Typography>
                    )}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={i18n.t("transferTicketModal.fieldLabel")}
                    variant="outlined"
                    autoFocus
                    onChange={(e) => setSearchParam(e.target.value)}
                    className={classes.autoComplete}
                    InputLabelProps={{
                      className: classes.inputLabel,
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {loading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item xs={12}>
              <FormControl
                variant="outlined"
                fullWidth
                className={classes.formControl}
              >
                <InputLabel className={classes.inputLabel}>
                  {i18n.t("transferTicketModal.fieldQueueLabel")}
                </InputLabel>
                <Select
                  value={selectedQueue}
                  onChange={(e) => setSelectedQueue(e.target.value)}
                  label={i18n.t("transferTicketModal.fieldQueuePlaceholder")}
                  className={classes.selectMenu}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    getContentAnchorEl: null,
                  }}
                  renderValue={(selected) => {
                    const queue = queues.find((q) => q.id === selected);
                    return (
                      <Box display="flex" alignItems="center">
                        <span
                          className={classes.queueColor}
                          style={queue?.color ? { backgroundColor: queue.color } : {}}
                        />
                        {queue ? queue.name : "Selecione uma fila"}
                      </Box>
                    );
                  }}
                >
                  {queues.map((queue) => (
                    <MenuItem
                      key={queue.id}
                      value={queue.id}
                      className={classes.menuItem}
                    >
                      <Box display="flex" alignItems="center">
                        <span
                          className={classes.queueColor}
                          style={queue.color ? { backgroundColor: queue.color } : {}}
                        />
                        {queue.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item xs={12}>
              <TextField
                label={i18n.t("transferTicketModal.msgTransfer")}
                value={msgTransfer}
                onChange={handleMsgTransferChange}
                variant="outlined"
                multiline
                rows={5}
                fullWidth
                className={classes.textField}
                InputLabelProps={{
                  className: classes.inputLabel,
                }}
              />
            </Grid>
          </Grid>
        </div>
      </div>
    </StandardModal>
  );
};

export default TransferTicketModalCustom;
